import React, { useState, useCallback, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    RefreshControl,
    ActivityIndicator,
    Modal,
    StatusBar,
    Dimensions,
    Platform,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import axios from 'axios';
import ConfettiCannon from 'react-native-confetti-cannon';
import { COLORS, API, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';
import { useCustomAlert } from '../components/CustomAlert';

export default function HadiahScreen() {
    const navigation = useNavigation();
    const { user, refreshUser } = useAuth();
    const { alert } = useCustomAlert();
    const [hadiah, setHadiah] = useState([]);
    const [myRedeemed, setMyRedeemed] = useState([]);
    const [activeTab, setActiveTab] = useState('catalog');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedHadiah, setSelectedHadiah] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [redeeming, setRedeeming] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const confettiRef = useRef(null);

    // Undian state
    const [undianModalVisible, setUndianModalVisible] = useState(false);
    const [undianAmount, setUndianAmount] = useState('1');
    const [exchangingUndian, setExchangingUndian] = useState(false);
    const [serverPeriode, setServerPeriode] = useState('');

    const fetchData = async () => {
        try {
            const [hadiahRes, redeemedRes, periodRes] = await Promise.all([
                axios.get(API.HADIAH.LIST),
                axios.get(API.HADIAH.MY_REDEEMED(user.id)).catch(() => ({ data: { success: true, data: [] } })),
                axios.get(API.UNDIAN.CURRENT_PERIOD).catch((err) => {
                    console.log('Period Fetch Error:', err);
                    return { data: { success: true, periode: null } };
                })
            ]);

            if (hadiahRes.data.success) setHadiah(hadiahRes.data.data || []);
            if (redeemedRes.data.success) setMyRedeemed(redeemedRes.data.data || []);
            if (periodRes.data.success && periodRes.data.periode) setServerPeriode(periodRes.data.periode);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            refreshUser();
            fetchData();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        refreshUser();
        fetchData();
    };

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const handleRedeem = (item) => {
        setSelectedHadiah(item);
        setModalVisible(true);
    };

    const confirmRedeem = async () => {
        if (!selectedHadiah) return;

        setRedeeming(true);
        try {
            const response = await axios.post(API.HADIAH.REDEEM, {
                member_id: user.id,
                hadiah_id: selectedHadiah.id,
            });

            if (response.data.success) {
                setModalVisible(false);
                setShowConfetti(true);
                if (confettiRef.current) {
                    confettiRef.current.start();
                }
                setTimeout(() => {
                    alert('🎉 Berhasil!', 'Hadiah berhasil ditukar! Tunjukkan ke kasir untuk claim.');
                    setShowConfetti(false);
                }, 500);
                refreshUser();
                fetchData();
            } else {
                alert('Gagal', response.data.message);
            }
        } catch (error) {
            alert('Error', 'Gagal terhubung ke server');
        } finally {
            setRedeeming(false);
        }
    };

    // Handle undian exchange
    const handleExchangeUndian = async () => {
        const jumlah = parseInt(undianAmount) || 0;
        if (jumlah < 1) {
            alert('Error', 'Minimal 1 poin untuk 1 undian');
            return;
        }
        if (jumlah > (user?.poin || 0)) {
            alert('Error', 'Poin tidak cukup');
            return;
        }

        setExchangingUndian(true);
        try {
            const response = await axios.post(API.UNDIAN.TUKAR, {
                member_id: user.id,
                jumlah: jumlah,
            }, { timeout: 15000 });

            if (response.data.success) {
                setUndianModalVisible(false);
                setShowConfetti(true);
                if (confettiRef.current) {
                    confettiRef.current.start();
                }
                setTimeout(() => {
                    alert(
                        '🎉 Berhasil!',
                        `Anda mendapatkan ${jumlah} lembar undian!\n\nNomor: ${response.data.data.nomor_undian.join(', ')}\n\nLihat di menu Voucher > Tab Undian`,
                        [{ text: 'OK' }]
                    );
                    setShowConfetti(false);
                }, 500);
                setUndianAmount('1');
                refreshUser();
            } else {
                alert('Gagal', response.data.message || 'Terjadi kesalahan saat menukar undian');
            }
        } catch (error) {
            let errorMessage = 'Gagal terhubung ke server';
            if (error.response) {
                // Server responded with error
                errorMessage = error.response.data?.message || `Server Error: ${error.response.status}`;
            } else if (error.request) {
                // Network error
                errorMessage = 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.';
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'Koneksi timeout. Silakan coba lagi.';
            }
            alert('Error', errorMessage);
            console.log('Undian Error:', error.message);
        } finally {
            setExchangingUndian(false);
        }
    };

    const getStatusStyle = (status) => {
        const styles = {
            pending: { bg: COLORS.warning + '15', color: COLORS.warning, text: 'Belum Claim' },
            approved: { bg: COLORS.info + '15', color: COLORS.info, text: 'Diproses' },
            completed: { bg: COLORS.success + '15', color: COLORS.success, text: 'Selesai' },
            rejected: { bg: COLORS.danger + '15', color: COLORS.danger, text: 'Ditolak' },
        };
        return styles[status] || styles.pending;
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            {/* Header */}
            <View style={styles.header}>
                <Text style={styles.headerTitle}>Hadiah</Text>
                <View style={styles.poinBadge}>
                    <Text style={styles.poinValue}>{formatNumber(user?.poin)} MP</Text>
                </View>
            </View>

            {/* Tabs */}
            <View style={styles.tabsContainer}>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'catalog' && styles.tabActive]}
                    onPress={() => setActiveTab('catalog')}
                >
                    <Text style={[styles.tabText, activeTab === 'catalog' && styles.tabTextActive]}>
                        Katalog
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'redeemed' && styles.tabActive]}
                    onPress={() => setActiveTab('redeemed')}
                >
                    <Text style={[styles.tabText, activeTab === 'redeemed' && styles.tabTextActive]}>
                        Hadiah Saya ({myRedeemed.length})
                    </Text>
                </TouchableOpacity>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={COLORS.primary}
                    />
                }
            >
                {activeTab === 'catalog' ? (
                    <>
                        <Text style={styles.subtitle}>Tukarkan {APP_INFO.currency} kamu!</Text>

                        {/* Undian Card */}
                        <View style={styles.undianCard}>
                            <View style={styles.undianContent}>
                                <Text style={styles.undianIcon}>🎫</Text>
                                <View style={styles.undianInfo}>
                                    <Text style={styles.undianTitle}>Undian Berhadiah</Text>
                                    <Text style={styles.undianDesc}>1 Poin = 1 Lembar Undian</Text>
                                </View>
                            </View>
                            <View style={styles.undianActions}>
                                <TouchableOpacity
                                    style={styles.undianBtnSecondary}
                                    onPress={() => navigation.navigate('Undian')}
                                >
                                    <Text style={styles.undianBtnSecondaryText}>Lihat</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.undianButton}
                                    onPress={() => setUndianModalVisible(true)}
                                >
                                    <Text style={styles.undianButtonText}>Tukar</Text>
                                </TouchableOpacity>
                            </View>
                        </View>

                        {hadiah.length === 0 ? (
                            <View style={styles.emptyState}>
                                <Text style={styles.emptyIcon}>🎁</Text>
                                <Text style={styles.emptyTitle}>Belum Ada Hadiah</Text>
                                <Text style={styles.emptyText}>Hadiah akan segera ditambahkan</Text>
                            </View>
                        ) : (
                            hadiah.map((item) => (
                                <View key={item.id} style={styles.hadiahCard}>
                                    <View style={styles.hadiahIcon}>
                                        <Text style={styles.hadiahEmoji}>{item.icon || '🎁'}</Text>
                                    </View>

                                    <View style={styles.hadiahContent}>
                                        <Text style={styles.hadiahName}>{item.nama}</Text>
                                        <Text style={styles.hadiahDesc} numberOfLines={2}>
                                            {item.deskripsi || 'Hadiah spesial untuk member'}
                                        </Text>
                                        <View style={styles.hadiahMeta}>
                                            <View style={styles.stokBadge}>
                                                <Text style={styles.stokText}>Stok: {item.stok}</Text>
                                            </View>
                                            <Text style={styles.hadiahPoin}>{formatNumber(item.poin_diperlukan)} MP</Text>
                                        </View>
                                    </View>

                                    {item.stok <= 0 ? (
                                        <View style={[styles.redeemBtn, styles.redeemDisabled]}>
                                            <Text style={styles.redeemDisabledText}>Habis</Text>
                                        </View>
                                    ) : parseInt(user?.poin || 0) < parseInt(item.poin_diperlukan || 0) ? (
                                        <View style={[styles.redeemBtn, styles.redeemDisabled]}>
                                            <Text style={styles.redeemDisabledText}>Kurang</Text>
                                        </View>
                                    ) : (
                                        <TouchableOpacity
                                            style={styles.redeemBtn}
                                            onPress={() => handleRedeem(item)}
                                        >
                                            <Text style={styles.redeemBtnText}>Tukar</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                            ))
                        )}
                    </>
                ) : (
                    <>
                        <View style={styles.infoBox}>
                            <Text style={styles.infoBoxIcon}>💡</Text>
                            <Text style={styles.infoBoxText}>
                                Tunjukkan hadiah kepada kasir untuk claim. Kasir akan memproses hadiah Anda.
                            </Text>
                        </View>

                        {myRedeemed.length === 0 ? (
                            <View style={styles.emptyState}>
                                <Text style={styles.emptyIcon}>📭</Text>
                                <Text style={styles.emptyTitle}>Belum Ada Hadiah</Text>
                                <Text style={styles.emptyText}>Tukarkan poin dengan hadiah menarik!</Text>
                            </View>
                        ) : (
                            myRedeemed.map((item, index) => {
                                const status = getStatusStyle(item.status);
                                return (
                                    <View key={index} style={styles.redeemedCard}>
                                        <View style={styles.redeemedIcon}>
                                            <Text style={styles.redeemedEmoji}>{item.icon || '🎁'}</Text>
                                        </View>
                                        <View style={styles.redeemedContent}>
                                            <Text style={styles.redeemedName}>{item.hadiah_nama}</Text>
                                            <Text style={styles.redeemedCode}>Kode: {item.kode_redeem || 'RDM' + item.id}</Text>
                                            <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                                                <Text style={[styles.statusText, { color: status.color }]}>{status.text}</Text>
                                            </View>
                                        </View>
                                        {item.status === 'pending' && (
                                            <View style={styles.claimBadge}>
                                                <Text style={styles.claimBadgeText}>Tunjukkan ke Kasir</Text>
                                            </View>
                                        )}
                                    </View>
                                );
                            })
                        )}
                    </>
                )}

                <View style={{ height: 100 }} />
            </ScrollView>

            {/* Redeem Modal */}
            <Modal
                animationType="fade"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalIcon}>
                            <Text style={styles.modalEmoji}>{selectedHadiah?.icon || '🎁'}</Text>
                        </View>

                        <Text style={styles.modalTitle}>Tukar Hadiah?</Text>
                        <Text style={styles.modalHadiah}>{selectedHadiah?.nama}</Text>

                        <View style={styles.modalPoinBox}>
                            <Text style={styles.modalPoinValue}>{formatNumber(selectedHadiah?.poin_diperlukan)}</Text>
                            <Text style={styles.modalPoinLabel}>{APP_INFO.currency}</Text>
                        </View>

                        <Text style={styles.modalNote}>
                            Setelah ditukar, tunjukkan ke kasir untuk claim hadiah Anda.
                        </Text>

                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={styles.modalBtnCancel}
                                onPress={() => setModalVisible(false)}
                                disabled={redeeming}
                            >
                                <Text style={styles.modalBtnCancelText}>Batal</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.modalBtnConfirm}
                                onPress={confirmRedeem}
                                disabled={redeeming}
                            >
                                {redeeming ? (
                                    <ActivityIndicator color="#fff" size="small" />
                                ) : (
                                    <Text style={styles.modalBtnConfirmText}>Tukar</Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Undian Modal */}
            <Modal
                visible={undianModalVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setUndianModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>🎫 Tukar Undian</Text>
                        <Text style={styles.modalDesc}>
                            1 Poin = 1 Lembar Undian{'\n'}
                            Periode: {serverPeriode || new Date().toLocaleString('id-ID', { month: 'long', year: 'numeric' })}
                        </Text>
                        <View style={styles.modalInfo}>
                            <Text style={styles.modalInfoLabel}>Poin Anda:</Text>
                            <Text style={styles.modalInfoValue}>{formatNumber(user?.poin)} MP</Text>
                        </View>
                        <View style={styles.undianInputRow}>
                            <TouchableOpacity
                                style={styles.undianInputBtn}
                                onPress={() => setUndianAmount(Math.max(1, parseInt(undianAmount || 1) - 1).toString())}
                            >
                                <Text style={styles.undianInputBtnText}>-</Text>
                            </TouchableOpacity>
                            <View style={styles.undianInputBox}>
                                <Text style={styles.undianInputValue}>{undianAmount}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.undianInputBtn}
                                onPress={() => setUndianAmount((parseInt(undianAmount || 0) + 1).toString())}
                            >
                                <Text style={styles.undianInputBtnText}>+</Text>
                            </TouchableOpacity>
                        </View>
                        <Text style={styles.undianCost}>
                            Akan menggunakan: {formatNumber(parseInt(undianAmount) || 0)} Poin
                        </Text>
                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={styles.modalBtnCancel}
                                onPress={() => setUndianModalVisible(false)}
                            >
                                <Text style={styles.modalBtnCancelText}>Batal</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.modalBtnConfirm}
                                onPress={handleExchangeUndian}
                                disabled={exchangingUndian}
                            >
                                {exchangingUndian ? (
                                    <ActivityIndicator color="#fff" size="small" />
                                ) : (
                                    <Text style={styles.modalBtnConfirmText}>Tukar</Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Confetti Animation */}
            {showConfetti && (
                <ConfettiCannon
                    ref={confettiRef}
                    count={150}
                    origin={{ x: Dimensions.get('window').width / 2, y: 0 }}
                    autoStart={true}
                    fadeOut={true}
                    fallSpeed={3000}
                    colors={[COLORS.primary, '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1']}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: Platform.OS === 'web' ? 20 : 50,
        paddingBottom: 16,
        paddingHorizontal: 20,
        backgroundColor: COLORS.bgDarker,
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    poinBadge: {
        backgroundColor: COLORS.primary + '15',
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
    },
    poinValue: {
        fontSize: 14,
        color: COLORS.primary,
        fontWeight: '700',
    },
    tabsContainer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: COLORS.bgDarker,
        gap: 10,
    },
    tab: {
        flex: 1,
        paddingVertical: 10,
        alignItems: 'center',
        borderRadius: 10,
        backgroundColor: COLORS.bgDark,
    },
    tabActive: {
        backgroundColor: COLORS.primary,
    },
    tabText: {
        color: COLORS.textSecondary,
        fontWeight: '600',
        fontSize: 13,
    },
    tabTextActive: {
        color: '#fff',
    },
    scrollView: {
        flex: 1,
        paddingHorizontal: 10,
        paddingTop: 12,
    },
    subtitle: {
        fontSize: 14,
        color: COLORS.textSecondary,
        marginBottom: 16,
    },
    infoBox: {
        flexDirection: 'row',
        backgroundColor: COLORS.primary + '10',
        borderRadius: 12,
        padding: 14,
        marginBottom: 16,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: COLORS.primary + '20',
    },
    infoBoxIcon: {
        fontSize: 24,
        marginRight: 12,
    },
    infoBoxText: {
        flex: 1,
        fontSize: 12,
        color: COLORS.textSecondary,
        lineHeight: 18,
    },
    hadiahCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        padding: 16,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
        borderLeftWidth: 4,
        borderLeftColor: COLORS.primary,
    },
    hadiahIcon: {
        width: 56,
        height: 56,
        borderRadius: 14,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 14,
    },
    hadiahEmoji: {
        fontSize: 28,
    },
    hadiahContent: {
        flex: 1,
    },
    hadiahName: {
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    hadiahDesc: {
        fontSize: 11,
        color: COLORS.textSecondary,
        marginBottom: 8,
    },
    hadiahMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    stokBadge: {
        backgroundColor: COLORS.bgDark,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    stokText: {
        fontSize: 10,
        color: COLORS.textSecondary,
        fontWeight: '500',
    },
    hadiahPoin: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.primary,
    },
    redeemBtn: {
        backgroundColor: COLORS.primary,
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 10,
    },
    redeemBtnText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 13,
    },
    redeemDisabled: {
        backgroundColor: COLORS.textMuted + '30',
    },
    redeemDisabledText: {
        color: COLORS.textMuted,
        fontWeight: '600',
        fontSize: 13,
    },
    redeemedCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        padding: 16,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    redeemedIcon: {
        width: 50,
        height: 50,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 14,
    },
    redeemedEmoji: {
        fontSize: 24,
    },
    redeemedContent: {
        flex: 1,
    },
    redeemedName: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    redeemedCode: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginBottom: 6,
    },
    statusBadge: {
        alignSelf: 'flex-start',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 10,
        fontWeight: '600',
    },
    claimBadge: {
        backgroundColor: COLORS.warning,
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderRadius: 8,
    },
    claimBadgeText: {
        color: '#fff',
        fontSize: 9,
        fontWeight: '700',
        textAlign: 'center',
    },
    emptyState: {
        alignItems: 'center',
        padding: 50,
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
    },
    emptyIcon: {
        fontSize: 48,
        marginBottom: 12,
        opacity: 0.5,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    emptyText: {
        color: COLORS.textSecondary,
        fontSize: 13,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    modalContent: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 20,
        padding: 24,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 20,
        elevation: 10,
    },
    modalIcon: {
        width: 80,
        height: 80,
        borderRadius: 20,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
    },
    modalEmoji: {
        fontSize: 40,
    },
    modalTitle: {
        fontSize: 14,
        color: COLORS.textSecondary,
        marginBottom: 8,
    },
    modalHadiah: {
        fontSize: 20,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 16,
        textAlign: 'center',
    },
    modalPoinBox: {
        backgroundColor: COLORS.primary + '10',
        paddingHorizontal: 24,
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
        marginBottom: 16,
    },
    modalPoinValue: {
        fontSize: 28,
        fontWeight: '700',
        color: COLORS.primary,
    },
    modalPoinLabel: {
        fontSize: 12,
        color: COLORS.textSecondary,
        marginTop: 2,
    },
    modalNote: {
        fontSize: 12,
        color: COLORS.textMuted,
        textAlign: 'center',
        marginBottom: 20,
    },
    modalButtons: {
        flexDirection: 'row',
        gap: 12,
        width: '100%',
    },
    modalBtnCancel: {
        flex: 1,
        padding: 14,
        borderRadius: 12,
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    modalBtnCancelText: {
        color: COLORS.textSecondary,
        fontWeight: '600',
        fontSize: 15,
    },
    modalBtnConfirm: {
        flex: 1,
        padding: 14,
        borderRadius: 12,
        alignItems: 'center',
        backgroundColor: COLORS.primary,
    },
    modalBtnConfirmText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 15,
    },
    // Undian Card Styles
    undianCard: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.accent,
        borderRadius: 14,
        padding: 14,
        marginBottom: 16,
    },
    undianContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    undianIcon: {
        fontSize: 32,
        marginRight: 12,
    },
    undianInfo: {
        flex: 1,
    },
    undianTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#1a1a1a',
        marginBottom: 2,
    },
    undianDesc: {
        fontSize: 12,
        color: '#555',
    },
    undianButton: {
        backgroundColor: COLORS.primary,
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
    },
    undianButtonText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 14,
    },
    undianActions: {
        flexDirection: 'row',
        gap: 8,
    },
    undianBtnSecondary: {
        backgroundColor: COLORS.bgDark,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: COLORS.primary,
    },
    undianBtnSecondaryText: {
        color: COLORS.primary,
        fontWeight: '600',
        fontSize: 14,
    },
    undianInputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginVertical: 16,
        gap: 12,
    },
    undianInputBtn: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: COLORS.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    undianInputBtnText: {
        color: '#fff',
        fontSize: 24,
        fontWeight: '700',
    },
    undianInputBox: {
        width: 80,
        height: 50,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: COLORS.primary,
    },
    undianInputValue: {
        fontSize: 24,
        fontWeight: '800',
        color: COLORS.primary,
    },
    undianCost: {
        textAlign: 'center',
        fontSize: 13,
        color: COLORS.textSecondary,
        marginBottom: 16,
    },
});
